import { PDF_JOB_CLIENT_MAX_WAIT_MS, PDF_JOB_CLIENT_POLL_INTERVAL_MS } from 'src/constants.performance';
import { fetchEndpointData } from '@utils/fetchEndpointData';
import type { IPdfJobRef } from 'src/queue/pdf-job-queues';
import type { IPdfJobStatusResponse } from 'src/queue/pdf-job-status';
import { sleep } from '@utils/sleep';

export class PdfJobTimeoutError extends Error {
  constructor(
    public readonly job: IPdfJobRef,
    public readonly maxWaitMs: number,
  ) {
    super(`PDF job ${job.queue}/${job.jobId} timed out after ${maxWaitMs}ms`);
    this.name = 'PdfJobTimeoutError';
  }
}

export class PdfJobFailedError extends Error {
  constructor(
    public readonly job: IPdfJobRef,
    public readonly failedReason: string,
  ) {
    super(`PDF job ${job.queue}/${job.jobId} failed: ${failedReason}`);
    this.name = 'PdfJobFailedError';
  }
}

export async function getPdfJobStatus(job: IPdfJobRef): Promise<IPdfJobStatusResponse> {
  return fetchEndpointData<IPdfJobStatusResponse>(`/api/v1/pdf-jobs/${job.queue}/${job.jobId}`);
}

/**
 * Poll a PDF Bull job from the browser until it completes, fails, or times out.
 */
export async function waitForPdfJobOnClient(
  job: IPdfJobRef,
  options?: { maxWaitMs?: number; pollIntervalMs?: number },
): Promise<IPdfJobStatusResponse> {
  const maxWaitMs = options?.maxWaitMs ?? PDF_JOB_CLIENT_MAX_WAIT_MS;
  const pollIntervalMs = options?.pollIntervalMs ?? PDF_JOB_CLIENT_POLL_INTERVAL_MS;
  const deadline = Date.now() + maxWaitMs;

  while (true) {
    const status = await getPdfJobStatus(job);
    if (status.status === 'completed') {
      return status;
    }
    if (status.status === 'failed') {
      throw new PdfJobFailedError(job, status.failedReason ?? 'Job failed');
    }
    if (status.status === 'unknown') {
      throw new PdfJobFailedError(job, 'Job not found');
    }
    if (Date.now() >= deadline) {
      throw new PdfJobTimeoutError(job, maxWaitMs);
    }
    await sleep(pollIntervalMs);
  }
}

/** Start a PDF job via GraphQL mutation result, wait on the client, then run an optional refresh. */
export async function runPdfJobAndRefresh(
  job: IPdfJobRef | null | undefined,
  refresh?: () => void | Promise<void>,
): Promise<void> {
  if (!job?.jobId || !job?.queue) {
    throw new Error('No PDF job returned from server');
  }
  await waitForPdfJobOnClient(job);
  await refresh?.();
}
